import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import API_BASE_URL from '../../config/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import ScheduleDayDialog from '../../components/ScheduleDayDialog';

export default function ScheduleCalendar() {
  const { year, month } = useParams();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState([]);
  const [workingDays, setWorkingDays] = useState(0);
  const [expectedHours, setExpectedHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userRole, setUserRole] = useState('');

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    // Get user role from localStorage
    const role = localStorage.getItem('role');
    setUserRole(role);
    fetchSchedule();
  }, [year, month]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/schedule/${year}/${month}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSchedule(data.dates || []);
        setWorkingDays(data.workingDays || 0);
        setExpectedHours(data.expectedHours || 0);
      } else {
        toast.error('Failed to fetch schedule');
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      toast.error('Error loading schedule');
    } finally {
      setLoading(false);
    }
  };

  const getDayType = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const scheduleEntry = schedule.find(s => s.Date.substring(0, 10) === dateStr);
    
    if (scheduleEntry) {
      return {
        type: scheduleEntry.DayType,
        reason: scheduleEntry.Reason
      };
    }
    
    // Default: Sunday is holiday, Mon-Sat is working day
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0) {
      return { type: 'DefaultSunday', reason: 'Default holiday' };
    }
    
    return { type: 'WorkingDay', reason: null };
  };

  // UI-only color mapping updated to dark theme (no blues, avoid #0b1220)
  const getDayColor = (dayType) => {
    switch (dayType) {
      case 'WorkingDay':
        return '#3d3d3f'; // dark gray for working days
      case 'Holiday':
        return '#3d1f1f'; // dark red for holidays
      case 'DefaultSunday':
        return '#3d1f1f'; // same as holiday
      case 'OpenSunday':
        return '#3d3a1f'; // muted golden for open sunday
      default:
        return '#3d3d3f';
    }
  };

  const getDayLabel = (dayType) => {
    switch (dayType) {
      case 'Holiday':
        return 'Holiday';
      case 'OpenSunday':
        return 'Open';
      case 'DefaultSunday':
        return 'Closed';
      default:
        return '';
    }
  };

  const handleDayClick = (date, dayType) => {
    if (userRole !== 'Admin') return; // Only admin can modify
    
    setSelectedDate({ date, dayType });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedDate(null);
  };

  const handleDialogAction = async () => {
    // Refresh schedule after action
    await fetchSchedule();
    handleDialogClose();
  };

  const renderCalendar = () => {
    const yearNum = parseInt(year);
    const monthNum = parseInt(month);
    const firstDay = new Date(yearNum, monthNum - 1, 1);
    const lastDay = new Date(yearNum, monthNum, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            aspectRatio: "1",
            border: "1px solid rgba(196,165,116,0.06)",
            bgcolor: "#3d3d3f",
          }}
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(yearNum, monthNum - 1, day);
      const { type: dayType, reason } = getDayType(date);
      const bgColor = getDayColor(dayType);
      const label = getDayLabel(dayType);
      const isClickable = userRole === 'Admin';

      days.push(
        <Box
          key={day}
          onClick={() => isClickable && handleDayClick(date, dayType)}
          sx={{
            aspectRatio: "1",
            border: "1px solid rgba(196,165,116,0.06)",
            bgcolor: bgColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            p: { xs: 0.25, sm: 0.5, md: 1 },
            transition: "all 0.2s",
            cursor: isClickable ? "pointer" : "default",
            "&:hover": isClickable ? {
              opacity: 0.95,
              transform: "scale(1.02)",
              boxShadow: 2,
            } : {},
          }}
        >
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: { xs: 2, sm: 4, md: 6 },
              left: { xs: 4, sm: 6, md: 8 },
              fontWeight: 600,
              color: "#C4A574",
              fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.85rem" },
            }}
          >
            {day}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {label && (
              <Typography
                variant="caption"
                sx={{
                  color: dayType === 'Holiday' || dayType === 'DefaultSunday' ? '#FF8A80' :
                         dayType === 'OpenSunday' ? '#C4A574' : '#E0E0E0',
                  fontWeight: 700,
                  fontSize: { xs: "0.45rem", sm: "0.6rem", md: "0.75rem" },
                  textAlign: "center",
                  lineHeight: 1,
                }}
              >
                {label.toUpperCase()}
              </Typography>
            )}
            {reason && dayType === 'Holiday' && (
              <Typography
                variant="caption"
                sx={{
                  color: "#d0cfcf",
                  fontWeight: 600,
                  fontSize: { xs: "0.4rem", sm: "0.55rem", md: "0.65rem" },
                  textAlign: "center",
                  mt: 0.25,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                {reason}
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/boutique-schedule')}
            sx={{
              mr: 2,
              bgcolor: '#8B7355',
              color: 'white',
              '&:hover': { bgcolor: '#6D5A43' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#C4A574',
              fontSize: { xs: '1.5rem', sm: '2rem', lg: '2.125rem' }
            }}
          >
            {monthNames[parseInt(month) - 1]} {year} - Boutique Schedule
          </Typography>
        </Box>

        {/* Stats Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ bgcolor: "#3d3d3f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#C4A574" }}>
                  {workingDays}
                </Typography>
                <Typography variant="body2" sx={{ color: "#d0cfcf" }}>
                  Working Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card sx={{ bgcolor: "#3d3d3d", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#C4A574" }}>
                  {expectedHours}
                </Typography>
                <Typography variant="body2" sx={{ color: "#d0cfcf" }}>
                  Expected Hours ({workingDays} × 9)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Chip
            label="Working Day"
            sx={{ bgcolor: "#3d3d3f", color: "#C4A574", fontWeight: 600 }}
          />
          <Chip
            label="Holiday"
            sx={{ bgcolor: "#3d1f1f", color: "#FFABAB", fontWeight: 600 }}
          />
          <Chip
            label="Opened Sunday"
            sx={{ bgcolor: "#3d3a1f", color: "#C4A574", fontWeight: 600 }}
          />
          <Chip
            label="Default Sunday"
            sx={{ bgcolor: "#3d1f1f", color: "#FFABAB", fontWeight: 600 }}
          />
        </Box>

        {/* Calendar */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2e2e2e" }}>
          <CardContent>
            {/* Day Headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0,
                mb: 1,
              }}
            >
              {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                <Box
                  key={day}
                  sx={{
                    textAlign: "center",
                    py: { xs: 0.5, sm: 1 },
                    bgcolor: "#8B7355",
                    border: "1px solid rgba(196,165,116,0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ 
                      fontWeight: 700, 
                      color: "#ffffff",
                      fontSize: { xs: '0.5rem', sm: '0.65rem', md: '0.75rem' }
                    }}
                  >
                    {day.substring(0, 3)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0,
              }}
            >
              {renderCalendar()}
            </Box>
          </CardContent>
        </Card>

        {userRole !== 'Admin' && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              color: '#999',
            }}
          >
            View-only mode. Only administrators can modify the schedule.
          </Typography>
        )}
      </Box>

      {/* Day Action Dialog */}
      {selectedDate && (
        <ScheduleDayDialog
          open={dialogOpen}
          date={selectedDate.date}
          dayType={selectedDate.dayType}
          onClose={handleDialogClose}
          onAction={handleDialogAction}
        />
      )}
    </AppLayout>
  );
}
